import { Input } from '@nike/frame-component-library';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { REQUIRED_FIELD } from '../../utils/validation/input-validation';
import '../ccm/Modal.css';
import { defaultCapacityException } from '../o2o-capabilities/sfs/constants';
import {
  ButtonBlack, ButtonRed, ButtonWhite, DateInput,
} from '../reusable';

import { clampInt, multipleDateRangeOverlaps } from './helpers';

const MAX_UNITS_LIMIT = 3000;

const isExceptionValid = (e) => (
  !!e && !!e.from && !!e.to && e.from <= e.to
  && Number.isInteger(e.maxUnits) && e.maxUnits >= 0
);

const SfsExceptionsModal = ({
  isOpen, storeAlias, initialExceptions, onCancel, onSave,
}) => {
  const [exceptions, setExceptions] = useState([]);

  useEffect(() => {
    if (isOpen) setExceptions(cloneDeep(initialExceptions || []));
  }, [isOpen, initialExceptions]);

  if (!isOpen) return null;

  const updateException = (index, patch) => {
    setExceptions((prev) => {
      const next = cloneDeep(prev);
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const addException = () => setExceptions((prev) => [...prev, { ...defaultCapacityException }]);

  const deleteException = (index) => setExceptions((prev) => {
    const next = cloneDeep(prev);
    next.splice(index, 1);
    return next;
  });

  const inFormOverlap = multipleDateRangeOverlaps(exceptions);
  const allValid = exceptions.every(isExceptionValid);
  const canSave = !inFormOverlap && allValid;

  return (
    <div className="modal-overlay">
      <div className="modal-content o2o-bulk-confirm-modal o2o-bulk-exceptions-modal">
        <h2 className="headline-2">{`Capacity Exceptions — ${storeAlias}`}</h2>
        <div className="o2o-bulk-confirm-body">
          <p className="o2o-bulk-empty-hint">
            Edit this store&apos;s capacity exceptions. Saved exceptions replace the store&apos;s existing list on submit.
          </p>

          <div className="o2o-bulk-exceptions-list">
            {exceptions.length === 0 && (
              <p className="o2o-bulk-empty-hint">No exceptions yet. Click &quot;Add Capacity Exception&quot; to create one.</p>
            )}
            {exceptions.map((exception, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <section key={i} className="o2o-bulk-exception-row">
                <DateInput
                  className="o2o-bulk-exception-from"
                  errorMessage={!exception.from ? REQUIRED_FIELD : ''}
                  id={`o2o-bulk-modal-from-${i}`}
                  label="From"
                  value={exception.from}
                  onChange={(from) => updateException(i, { from })}
                />
                <DateInput
                  className="o2o-bulk-exception-to"
                  errorMessage={!exception.to ? REQUIRED_FIELD : ''}
                  id={`o2o-bulk-modal-to-${i}`}
                  label="To"
                  value={exception.to}
                  onChange={(to) => updateException(i, { to })}
                />
                <div className="o2o-bulk-exception-max">
                  <label htmlFor={`o2o-bulk-modal-max-${i}`}>Max Units</label>
                  <Input
                    id={`o2o-bulk-modal-max-${i}`}
                    max={MAX_UNITS_LIMIT}
                    min="0"
                    step="1"
                    type="number"
                    value={exception.maxUnits}
                    onChange={({ target }) => {
                      const clamped = clampInt(target.value, 0, MAX_UNITS_LIMIT);
                      updateException(i, { maxUnits: clamped == null ? 0 : clamped });
                    }}
                  />
                </div>
                <ButtonRed
                  className="o2o-bulk-exception-delete"
                  confirm={false}
                  label={<i aria-hidden="true" className="g72-x-thick" />}
                  onClick={() => deleteException(i)}
                />
                {exception.from && exception.to && exception.from > exception.to && (
                  <aside className="o2o-bulk-row-warning">From date must be on or before To date.</aside>
                )}
              </section>
            ))}
          </div>

          {inFormOverlap && (
            <aside className="o2o-bulk-row-warning">Error: Capacity Exceptions must not overlap with each other.</aside>
          )}

          <ButtonBlack
            className="o2o-bulk-exception-add"
            label="Add Capacity Exception"
            onClick={addException}
          />
        </div>
        <div className="o2o-bulk-confirm-actions">
          <ButtonWhite className="border" label="Cancel" onClick={onCancel} />
          <ButtonBlack
            isDisabled={!canSave}
            label="Save"
            onClick={() => onSave(exceptions)}
          />
        </div>
      </div>
    </div>
  );
};

SfsExceptionsModal.defaultProps = {
  initialExceptions: [],
};

SfsExceptionsModal.propTypes = {
  initialExceptions: PropTypes.arrayOf(PropTypes.shape()),
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  storeAlias: PropTypes.string.isRequired,
};

export default SfsExceptionsModal;
